import React, { useRef, useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaDownload,
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaMapMarkerAlt,
  FaSpinner,
} from "react-icons/fa";
import emailjs from "@emailjs/browser";
import cv from "../components/CV/Mirzaherenda.pdf";
import { Eyebrow, Magnetic, Reveal } from "./motion";

const socials = [
  { icon: FaGithub, href: "https://github.com/Mirza-herenda", label: "GitHub" },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/in/mirza-herenda-6710071a0/",
    label: "LinkedIn",
  },
];

const field =
  "w-full rounded-xl border border-slate-800 bg-slate-800/50 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-primary-500/50 focus:bg-slate-800 focus:ring-2 focus:ring-primary-500/30";

function ContactForm({ setIsOpen }) {
  const form = useRef();
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = cv;
    link.download = "MIRZAHERENDACV.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    emailjs
      .sendForm(
        "service_uhx4ral",
        "template_ujhumre",
        form.current,
        "agBHxvYVU_yl2mIGQ"
      )
      .then(
        () => {
          form.current.reset();
          setStatus("sent");
        },
        () => {
          setError("Failed to send message. Please try again.");
          setStatus("idle");
        }
      );
  };

  return (
    <section
      id="contact"
      className="relative bg-dark-bg px-4 py-24 sm:px-6 lg:py-32"
      onClick={() => setIsOpen(true)}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/4 -z-10 mx-auto h-72 max-w-3xl rounded-full bg-primary-500/10 blur-[120px]"
      />
      <div className="mx-auto max-w-6xl 2xl:max-w-7xl">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h2 className="mt-6 max-w-2xl text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-[1.05] text-transparent bg-gradient-to-r from-primary-400 to-sky-400 bg-clip-text font-display">
            Have something in mind? Let's build it.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
          {/* details */}
          <Reveal className="flex flex-col gap-5">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-6 sm:p-7">
              <p className="text-sm leading-relaxed text-slate-400">
                Whether it's a full product, a mobile app or a redesign —
                tell me the problem and I'll tell you honestly how I'd solve
                it. Replies usually within a day.
              </p>

              <ul className="mt-7 space-y-4">
                <li>
                  <a
                    href="mailto:herendamirza87@gmail.com"
                    className="group grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3.5 underline decoration-transparent underline-offset-4 transition-colors duration-300 hover:decoration-primary-400"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-primary-500/25 bg-primary-500/10 transition-colors duration-300 group-hover:bg-primary-500/20">
                      <FaEnvelope className="text-sm text-primary-400" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                        Email
                      </span>
                      <span className="block truncate text-sm text-slate-300 transition-colors duration-300 group-hover:text-primary-400">
                        herendamirza87@gmail.com
                      </span>
                    </span>
                  </a>
                </li>
                <li className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-slate-800 bg-slate-800/60">
                    <FaMapMarkerAlt className="text-sm text-primary-400" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                      Location
                    </span>
                    <span className="block truncate text-sm text-slate-300">
                      Sarajevo, Bosnia &amp; Herzegovina
                    </span>
                  </span>
                </li>
              </ul>

              <div className="mt-7 flex gap-2.5">
                {socials.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="group grid h-10 w-10 place-items-center rounded-xl border border-slate-800 bg-slate-900/50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-500/45 hover:shadow-[0_0_30px_-6px_rgba(14,165,233,0.5)]"
                  >
                    <Icon className="text-sm text-slate-400 transition-colors duration-300 group-hover:text-primary-400" />
                  </a>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-6">
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-100 font-display">
                  Prefer the short version?
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Grab my resume instead.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-800 bg-slate-800/60 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-primary-500/45 hover:text-primary-400"
              >
                <FaDownload className="text-sm" />
                CV
              </button>
            </div>
          </Reveal>

          {/* form */}
          <Reveal delay={120}>
            <div className="relative h-full overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-6 shadow-xl sm:p-9">
              {status === "sent" ? (
                <div className="flex min-h-[22rem] flex-col items-center justify-center text-center">
                  <span className="grid h-16 w-16 place-items-center rounded-2xl border border-primary-500/30 bg-primary-500/10">
                    <FaCheck className="text-2xl text-primary-400" />
                  </span>
                  <h3 className="mt-6 text-2xl font-bold text-slate-100 font-display">
                    Message sent
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                    Thanks for reaching out — I'll get back to you within a
                    day. You can also write me directly at
                    herendamirza87@gmail.com.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-7 inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-800/60 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-primary-500/45 hover:text-primary-400"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form
                  ref={form}
                  onSubmit={sendEmail}
                  className="flex flex-col gap-5"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="user_name"
                        className="mb-2 block font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase"
                      >
                        Name
                      </label>
                      <input
                        id="user_name"
                        name="user_name"
                        required
                        placeholder="Your name"
                        className={field}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="user_email"
                        className="mb-2 block font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase"
                      >
                        Email
                      </label>
                      <input
                        id="user_email"
                        name="user_email"
                        type="email"
                        required
                        placeholder="you@company.com"
                        className={field}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="mb-2 block font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={7}
                      placeholder="Tell me about the project, timeline and what success looks like."
                      className={`${field} resize-none`}
                    />
                  </div>

                  {error && (
                    <p className="text-sm font-semibold text-red-400">
                      {error}
                    </p>
                  )}

                  <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
                    <p className="text-xs leading-snug text-slate-500">
                      No spam, no sales lists — just a direct reply.
                    </p>
                    <Magnetic strength={0.2}>
                      <button
                        type="submit"
                        disabled={status === "sending"}
                        className="group inline-flex items-center gap-2 rounded-xl bg-primary-600 px-6 py-3.5 font-semibold text-white transition-shadow duration-300 hover:shadow-[0_0_30px_-6px_rgba(14,165,233,0.5)] disabled:opacity-70"
                      >
                        {status === "sending" && (
                          <FaSpinner className="animate-spin text-sm" />
                        )}
                        Send message
                        <FaArrowRight className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    </Magnetic>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default ContactForm;
